import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil } from "lucide-react";
import { z } from "zod";

import { SelectField, TextField } from "components/AddVaccineForm/components";
import { CardWithTitle } from "components/helpers/CardWithTitle/CardWithTitle.jsx";
import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { usePatientPreferences } from "features/patients/hooks/usePatientPreferences";
import { useSavePatientPreferences } from "features/patients/hooks/useSavePatientPreferences";
import { languageOptions } from "utils/languageOptions.js";
import { onInvalidSubmit } from "utils/reportFormErrors";

import s from "./Preferences.module.css";

const formSchema = z.object({
  mainLanguage: z.string().optional(),
  religion: z.string().optional(),
  spiritualPractices: z.string().optional(),
  culture: z.string().optional(),
});

const FIELDS = [
  { name: "mainLanguage", label: "Language" },
  { name: "religion", label: "Religion" },
  { name: "spiritualPractices", label: "Spiritual Practices" },
  { name: "culture", label: "Culture" },
];

const Preferences = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const patientId = useCurrentPatientId();
  const { data: preferences, refetch } = usePatientPreferences(patientId);
  const savePreferences = useSavePatientPreferences();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      mainLanguage: "",
      religion: "",
      spiritualPractices: "",
      culture: "",
    },
  });

  useEffect(() => {
    if (preferences) {
      form.reset({
        mainLanguage: preferences.mainLanguage || "",
        religion: preferences.religion || "",
        spiritualPractices: preferences.spiritualPractices || "",
        culture: preferences.culture || "",
      });
    }
  }, [preferences]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = (data) => {
    savePreferences.mutate(
      {
        id: preferences?.id || null,
        patientId: Number(patientId),
        ...data,
      },
      {
        onSuccess: () => {
          toast.success("Preferences saved successfully");
          setIsModalOpen(false);
          refetch();
        },
        onError: (error) => {
          console.error("Failed to save preferences:", error);
          toast.error("Failed to save preferences. Please try again.");
        },
      }
    );
  };

  const getDisplayValue = (field) => {
    if (!preferences) return "-";
    const value = preferences[field.name];
    if (!value) return "-";
    if (field.name === "mainLanguage") {
      return (
        languageOptions?.find((opt) => opt?.value === value)?.label || value
      );
    }
    return value;
  };

  return (
    <>
      <CardWithTitle
        title="Preferences"
        endIcon={Pencil}
        buttonTitle="Edit"
        className={s.editButton}
        showButton={false}
        onButtonClick={() => setIsModalOpen(true)}
      >
        <ul className={s.listContainer}>
          {FIELDS.map((field) => (
            <li key={field.name} className={s.listItemContainer}>
              <p className={s.listItemTitle}>{field.label}:</p>
              <p className={s.listItemDescr}>{getDisplayValue(field)}</p>
            </li>
          ))}
        </ul>
      </CardWithTitle>

      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Preferences"
        width={700}
      >
        <FormProvider {...form}>
          <form
            onSubmit={form.handleSubmit(handleSave, onInvalidSubmit("Preferences"))}
            className={s.formContainer}
          >
            <div className={s.formGrid}>
              <SelectField
                name="mainLanguage"
                label="Language"
                options={languageOptions}
                placeholder="Select language"
              />
              <TextField
                name="religion"
                label="Religion"
                placeholder="Example: Christian"
              />
              <TextField
                name="spiritualPractices"
                label="Spiritual Practices"
                placeholder="Example: Hindu"
              />
              <TextField
                name="culture"
                label="Culture"
                placeholder="Example: Chinese"
              />
            </div>
          </form>
        </FormProvider>
        <DialogFooter>
          <Button
            variant="outline"
            size="large"
            onClick={() => setIsModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="large"
            onClick={form.handleSubmit(handleSave, onInvalidSubmit("Preferences"))}
            disabled={savePreferences.isPending}
          >
            {savePreferences.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  );
};

export { Preferences };
